import { ANCHOR_TYPE, TARGETING_ATTRIBUTE, TARGETING_OPERATOR } from '../constants'
import { isPinningAnchorType } from '../hooks/anchorConstraints'
import { createEmptyTargetingRow } from '../models/createEmptyTargetingRow'

const flattenVisits = arms => {
  const visits = []
  ;(arms || []).forEach(arm => {
    ;(arm.phases || []).forEach(phase => {
      ;(phase.cycles || []).forEach(cycle => {
        ;(cycle.visits || []).forEach(visit => {
          visits.push({
            visitId: String(visit.id),
            cycleId: String(cycle.id),
            phaseId: String(phase.id),
            armId: String(arm.id),
          })
        })
      })
    })
  })
  return visits
}

const flattenCycles = arms => {
  const cycles = []
  ;(arms || []).forEach(arm => {
    ;(arm.phases || []).forEach(phase => {
      ;(phase.cycles || []).forEach(cycle => {
        cycles.push({
          cycleId: String(cycle.id),
          phaseId: String(phase.id),
          armId: String(arm.id),
        })
      })
    })
  })
  return cycles
}

const flattenPhases = arms => {
  const phases = []
  ;(arms || []).forEach(arm => {
    ;(arm.phases || []).forEach(phase => {
      phases.push({ phaseId: String(phase.id), armId: String(arm.id) })
    })
  })
  return phases
}

export const resolvePinningFromAnchor = (anchor, entityLookup = {}) => {
  const type = anchor?.type
  const instanceId = String(anchor?.instanceId || anchor?.instanceIds?.[0] || '')
  if (!isPinningAnchorType(type) || !instanceId) return null
  const arms = entityLookup.arms || []
  if (type === ANCHOR_TYPE.arm) return { armId: instanceId, phaseId: '' }
  if (type === ANCHOR_TYPE.phase) {
    const match = flattenPhases(arms).find(row => row.phaseId === instanceId)
    return match ? { armId: match.armId, phaseId: match.phaseId } : { armId: '', phaseId: instanceId }
  }
  if (type === ANCHOR_TYPE.visitCycle) {
    const match = flattenCycles(arms).find(row => row.cycleId === instanceId)
    return match ? { armId: match.armId, phaseId: match.phaseId } : null
  }
  if (type === ANCHOR_TYPE.visit) {
    const visitRef = anchor?.visitRefs?.[0]
    if (visitRef?.armId || visitRef?.arm_id) {
      const armId = String(visitRef.armId || visitRef.arm_id)
      const phaseId = visitRef.phaseId || visitRef.phase_id
        ? String(visitRef.phaseId || visitRef.phase_id)
        : ''
      if (!phaseId) {
        const match = flattenVisits(arms).find(row => row.visitId === instanceId)
        return match ? { armId: match.armId, phaseId: match.phaseId } : { armId, phaseId: '' }
      }
      return { armId, phaseId }
    }
    const match = flattenVisits(arms).find(row => row.visitId === instanceId)
    return match ? { armId: match.armId, phaseId: match.phaseId } : null
  }
  return null
}

const upsertLockedRow = (targeting, attribute, value) => {
  const existing = targeting.find(row => row.attribute === attribute && row.lockedByAnchor)
  if (existing) {
    return targeting.map(row =>
      row === existing ? { ...row, value, operator: TARGETING_OPERATOR.equals, lockedByAnchor: true } : row,
    )
  }
  return [
    ...targeting,
    createEmptyTargetingRow({
      attribute,
      operator: TARGETING_OPERATOR.equals,
      value,
      lockedByAnchor: true,
    }),
  ]
}

export const applyAnchorLocks = (rule, entityLookup, instrumentPinning) => {
  const anchors = rule.combineAnchors ? rule.anchors : [rule.anchors[0]]
  const pinningSources = anchors
    .map(anchor => {
      if (anchor.type === ANCHOR_TYPE.instrument) return instrumentPinning || null
      return resolvePinningFromAnchor(anchor, entityLookup)
    })
    .filter(Boolean)

  let targeting = (rule.targeting || []).map(row => ({ ...row, lockedByAnchor: false }))
  if (!pinningSources.length) {
    return { ...rule, targeting }
  }

  // Drop arm/phase audience rows from legacy cohort imports; anchor pinning replaces them.
  targeting = targeting.filter(
    row =>
      row.attribute !== TARGETING_ATTRIBUTE.arm && row.attribute !== TARGETING_ATTRIBUTE.periodPhase,
  )

  const pinning = pinningSources[0]
  if (pinning.armId) targeting = upsertLockedRow(targeting, TARGETING_ATTRIBUTE.arm, pinning.armId)
  if (pinning.phaseId) targeting = upsertLockedRow(targeting, TARGETING_ATTRIBUTE.periodPhase, pinning.phaseId)
  return { ...rule, targeting }
}
